import { defineStore } from 'pinia'
import type { GiveawayRoundResponse } from '@/services/models/response'
import { notify } from '@/utils/notify'
import { i18n } from '@/i18n'
import { getGiveaways, joinGiveaway as joinGiveawayRequest } from '@/services/api/requests/giveaways'
import type { GiveawayHistoryEntry, GiveawayHistoryResponse } from '@/services/models/giveaways'

export interface GiveawayWinnerEntry {
  roundId: string
  winner: {
    id: string
    name: string
    avatar?: string | null
  }
}

export type GiveawayResult = GiveawayHistoryEntry

interface GiveawaysState {
  giveawayRounds: GiveawayRoundResponse[]
  currentGiveawayRound: GiveawayRoundResponse | null
  loading: boolean
  loadFailed: boolean
  winnersList: GiveawayWinnerEntry[] // Local state to show winner modal if the current user won
  history: GiveawayHistoryResponse
}

export enum GiveawayRoundStatus {
  WAITING = 0,
  ACTIVE = 10,
  PICKING = 20,
  DONE = 30,
  ERROR = 40,
}

function mergeGiveawayType(
  existing: GiveawayRoundResponse['giveawayType'] | undefined,
  incoming: GiveawayRoundResponse['giveawayType'],
): GiveawayRoundResponse['giveawayType'] {
  return {
    ...(existing ?? {}),
    ...incoming,
    owner: incoming.owner ?? existing?.owner ?? null,
    ownerUserId: incoming.ownerUserId ?? existing?.ownerUserId,
    requiredReferralCodeId:
      incoming.requiredReferralCodeId ?? existing?.requiredReferralCodeId ?? null,
    requiredReferralCode:
      incoming.requiredReferralCode ?? existing?.requiredReferralCode ?? null,
    minimumTickets: incoming.minimumTickets ?? existing?.minimumTickets,
    maxTickets: incoming.maxTickets ?? existing?.maxTickets,
    isRecurring: incoming.isRecurring ?? existing?.isRecurring,
  }
}

function mergeGiveawayRound(
  existing: GiveawayRoundResponse | undefined,
  incoming: GiveawayRoundResponse,
): GiveawayRoundResponse {
  if (!existing) return incoming

  return {
    ...existing,
    ...incoming,
    minimumTickets: incoming.minimumTickets ?? existing.minimumTickets,
    endsAt: incoming.endsAt ?? existing.endsAt,
    userTickets: incoming.userTickets ?? existing.userTickets,
    totalTickets: incoming.totalTickets ?? existing.totalTickets,
    userParticipationsCount: incoming.userParticipationsCount ?? existing.userParticipationsCount,
    userCooldownUntil: incoming.userCooldownUntil ?? existing.userCooldownUntil,
    fairness: incoming.fairness ?? existing.fairness ?? null,
    participants: incoming.participants ?? existing.participants,
    winner: incoming.winner ?? existing.winner,
    giveawayType: mergeGiveawayType(existing.giveawayType, incoming.giveawayType),
  }
}

export const useGiveawaysStore = defineStore('giveaways', {
  state: (): GiveawaysState => ({
    giveawayRounds: [],
    currentGiveawayRound: null,
    loading: false,
    loadFailed: false,
    winnersList: [],
    history: {
      best: [],
      recent: [],
    },
  }),

  actions: {
    setGiveawayRounds(rounds: GiveawayRoundResponse[]) {
      this.giveawayRounds = rounds.map((round) => {
        const existing = this.giveawayRounds.find((entry) => entry.id === round.id)
        return mergeGiveawayRound(existing, round)
      })
    },

    setLoading(value: boolean) {
      this.loading = value
    },

    setLoadFailed(value: boolean) {
      this.loadFailed = value
    },

    // services
    async fetchGiveaways() {
      if (this.loading) return

      this.setLoading(true)
      this.setLoadFailed(false)

      try {
        const response = await getGiveaways()
        this.setGiveawayRounds(response?.giveaways ?? [])
        this.history = response.history ?? { recent: [], best: [] }
      } catch (error) {
        console.error('Failed to fetch giveaways', error)
        this.setLoadFailed(true)
      } finally {
        this.setLoading(false)
      }
    },

    async refreshGiveawaysSilently() {
      try {
        const response = await getGiveaways()
        this.setGiveawayRounds(response?.giveaways ?? [])
        this.history = response.history ?? { recent: [], best: [] }
      } catch (error) {
        console.error('Failed to refresh giveaways', error)
      }
    },

    async refreshGiveawayHistory() {
      try {
        // TODO create a history-only API. For now, only sync the history field.
        const response = await getGiveaways()
        this.history = response.history ?? { recent: [], best: [] }
      } catch (error) {
        console.error('Failed to refresh giveaway history', error)
      }
    },
    async joinGiveaway(roundId: string) {
      try {
        const response = await joinGiveawayRequest(roundId)

        if (response?.success) {
          notify('Successfully joined the giveaway!', 'success')

          // Update local state - mark giveaway as joined (participant count will come via SSE)
          const roundIndex = this.giveawayRounds.findIndex((round) => round.id === roundId)
          if (roundIndex !== -1) {
            this.giveawayRounds[roundIndex] = {
              ...this.giveawayRounds[roundIndex],
              userJoined: true,
            }
          }
        }

        // @ts-ignore - Deposit Check
        if (!response?.success && response.error === 'INSUFFICIENT_DEPOSIT') {
          notify(i18n.global.t('GIVEAWAYS.siteGiveaways.errors.insufficientDeposit'), 'error')
        }
        // @ts-ignore - Level Check
        if (!response?.success && response.error === 'INSUFFICIENT_LEVEL') {
          notify(i18n.global.t('GIVEAWAYS.siteGiveaways.errors.insufficientLevel'), 'error')
        }
        // @ts-ignore - Steam Level Check
        if (!response?.success && response.error === 'INSUFFICIENT_STEAM_LEVEL') {
          notify(i18n.global.t('GIVEAWAYS.siteGiveaways.errors.insufficientSteamLevel'), 'error')
        }
        // @ts-ignore - Email Not Verified Check
        if (!response?.success && response.error === 'EMAIL_NOT_VERIFIED') {
          notify(i18n.global.t('GIVEAWAYS.siteGiveaways.errors.emailNotVerified'), 'error')
        }

        return response
      } catch (error) {
        console.error('Failed to join giveaway', error)
        notify(i18n.global.t('GIVEAWAYS.siteGiveaways.errors.joinFailed'), 'error')
        return null
      }
    },

    addWinner(entry: GiveawayWinnerEntry) {
      const filtered = this.winnersList.filter((existing) => existing.roundId !== entry.roundId)
      this.winnersList = [entry, ...filtered]
    },

    getWinnerByRoundId(roundId: string) {
      return this.winnersList.find((entry) => entry.roundId === roundId)?.winner ?? null
    },

    setCurrentGiveawayRound(round: GiveawayRoundResponse | null) {
      this.currentGiveawayRound = round
    },

    upsertGiveawayRound(round: GiveawayRoundResponse) {
      const existingIndex = this.giveawayRounds.findIndex((entry) => entry.id === round.id)
      if (existingIndex === -1) {
        this.giveawayRounds = [round, ...this.giveawayRounds]
        return
      }

      this.giveawayRounds[existingIndex] = mergeGiveawayRound(this.giveawayRounds[existingIndex], round)
    },
  },
})
